import create from 'zustand';
import axios from '../../api/axios';

export interface AiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface AiConversation {
  id: string;
  title: string;
  messages: AiMessage[];
  createdAt: Date;
  updatedAt: Date;
}

interface AiChatState {
  conversations: AiConversation[];
  activeConversationId: string | null;
  isTyping: boolean;

  // Actions
  setActiveConversation: (id: string | null) => void;
  createConversation: () => AiConversation;
  createAiConversation: () => AiConversation;
  addMessage: (conversationId: string, message: AiMessage) => void;
  updateConversationTitle: (conversationId: string, title: string) => void;
  deleteConversation: (conversationId: string) => void;
  setTyping: (isTyping: boolean) => void;
  clearConversations: () => void;
  setConversations: (conversations: AiConversation[]) => void;
}

export const useAiChatStore = create<AiChatState>((set) => ({
  conversations: [],
  activeConversationId: null,
  isTyping: false,

  setActiveConversation: (id: string | null) => set({ activeConversationId: id }),

  createAiConversation: () => {
    const newConversation: AiConversation = {
      id: `ai-${Date.now()}`, // Temporary ID until API call completes
      title: 'New AI Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state) => ({
      conversations: [newConversation, ...state.conversations],
      activeConversationId: newConversation.id,
    }));

    // Only create session, user should already exist
    (async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const session = await axios.post(
          '/ai/sessions',
          {
            title: 'Project Assistant Conversation',
            kind: 'project_assistant',
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-User-ID': userData.id,
            },
          },
        );

        // Update conversation with real session ID
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === newConversation.id ? { ...conv, id: session.data.id } : conv,
          ),
          activeConversationId: session.data.id,
        }));
      } catch (error) {
        console.error('Error creating AI conversation:', error);
      }
    })();

    return newConversation;
  },

  createConversation: () => {
    const newConversation: AiConversation = {
      id: `conv-${Date.now()}`,
      title: 'New Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state) => ({
      conversations: [newConversation, ...state.conversations],
      activeConversationId: newConversation.id,
    }));

    return newConversation;
  },

  addMessage: (conversationId: string, message: AiMessage) =>
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              messages: [...conv.messages, message],
              updatedAt: new Date(),
              title:
                conv.messages.length === 0 && message.role === 'user'
                  ? message.content.substring(0, 30) + (message.content.length > 30 ? '...' : '')
                  : conv.title,
            }
          : conv,
      ),
    })),

  updateConversationTitle: (conversationId: string, title: string) =>
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, title, updatedAt: new Date() } : conv,
      ),
    })),

  deleteConversation: (conversationId: string) =>
    set((state) => ({
      conversations: state.conversations.filter((conv) => conv.id !== conversationId),
      activeConversationId:
        state.activeConversationId === conversationId ? null : state.activeConversationId,
    })),

  setTyping: (isTyping: boolean) => set({ isTyping }),

  clearConversations: () => set({ conversations: [], activeConversationId: null }),

  setConversations: (conversations: AiConversation[]) =>
    set({
      conversations,
      activeConversationId: conversations.length > 0 ? conversations[0].id : null,
    }),
}));
